import { addLocationToMeta } from './metaUtils';
import { extractCityFromLocation, extractStateFromLocation } from './location';
import { getDisplayStoreName } from './store';
import { convertTimestampToDate } from './date';
import { LOCATION_FOR_BOT } from '../hooks/useLocationDetect';

function splitTitle(string) {
  return string.split('|');
}

// Used by isomorphic rendering system to generate meta tags
const meta = {
  DEFAULT: {
    titleEn: 'Flipp - Latest Flyers and Best Deals',
    titleEnUs: 'Flipp - Latest Weekly Ads and Best Deals',
    titleFr: 'Flipp - Dernières circulaires et meilleures offres',
    descriptionEn:
      'Meet Flipp, the digital platform that will supercharge your savings on groceries, electronics and more. Browse the best deals and the latest flyers from over 1000 stores.',
    descriptionEnUs:
      'Meet Flipp, the digital platform that will supercharge your savings on groceries, electronics and more. Browse the best deals and the latest weekly ads from over 1000 stores.',
    descriptionFr:
      "Découvrez Flipp, la plateforme numérique qui vous fera économiser davantage sur l'épicerie, l'électronique et plus. Parcourez les meilleures offres et les dernières circulaires de plus de 1000 magasins.",
    openGraph: {
      getImage() {
        return `/assets/stores.png`;
      },
    },
    buildCanonical() {
      return window && window.location.href;
    },
    noIndex: false,
    getTitle(metaObj, locale) {
      return locale === 'fr-ca' && metaObj.titleFr
        ? metaObj.titleFr
        : locale === 'en-ca'
        ? metaObj.titleEn
        : metaObj.titleEnUs;
    },
    getDescription(metaObj, locale) {
      return locale === 'fr-ca' && metaObj.descriptionFr
        ? metaObj.descriptionFr
        : locale === 'en-ca'
        ? metaObj.descriptionEn
        : metaObj.descriptionEnUs;
    },
  },
  '/store-city-flyers': {
    titleEn: 'Browse Store Flyers by City | Flipp',
    titleEnUs: 'Browse Store Weekly Ads by City | Flipp',
    titleFr: 'Parcourez les circulaires des magasins par ville | Flipp',
  },
  '/store-city-weekly_ads': {
    titleEnUs: 'Browse Store Weekly Ads by City | Flipp',
  },
  '/flyers-canada': {
    titleEn: 'Canadian Flyers Collection | Browse Flyers with Flipp',
    titleFr: 'Collection de circulaires canadiennes | Parcourir les circulaires sur tout le Canada',
    descriptionEn:
      'Browse up-to-date, weekly flyers from the top retailers in Canada with Flipp. Shop smarter, download the Flipp app today!',
    descriptionFr:
      "Parcourez les circulaires hebdomadaires des meilleurs détaillants au Canada avec Flipp. Achetez intelligemment, téléchargez l'application Flipp dès aujourd'hui!",
  },
  '/flyers-usa': {
    titleEnUs: 'USA Weekly Ads Collection | Browse Weekly Ads with Flipp',
    descriptionEnUs:
      'Browse up-to-date, weekly ads from the top retailers in USA with Flipp. Shop smarter, download the Flipp app today!',
  },
  '/.*/flyers.*': {
    descriptionEn:
      'View all [location] flyers and find the best deals in [location]. Get deals on groceries, electronics, appliances and more.',
    descriptionEnUs:
      'View all [location] weekly ads and find the best deals in [location]. Get deals on groceries, electronics, appliances and more.',
    descriptionFr:
      "Consultez toutes les circulaires à [location]. Découvrez des rabais en épicerie, l'électronique, l'électroménager et plus.",
    getTitle(metaObj, locale, t, { location }) {
      return t('metaInfo.locationFlyersTitle', {
        city: extractCityFromLocation(location),
      });
    },
    getDescription(metaObj, locale, t, { location }) {
      const locationName =
        (location && `${extractCityFromLocation(location)}, ${extractStateFromLocation(location)}`) || '';
      const res =
        locale === 'fr-ca' && metaObj.descriptionFr
          ? metaObj.descriptionFr
          : locale === 'en-ca'
          ? metaObj.descriptionEn
          : metaObj.descriptionEnUs;
      return addLocationToMeta(res, locationName);
    },
    openGraph: {
      getImage() {
        return `/landing/flyers-us.jpeg`;
      },
      getTitle(query, t) {
        const location = query['location'];
        const locationName =
          (location && `${extractCityFromLocation(location)}, ${extractStateFromLocation(location)}`) || '';

        return t('metaInfo.og.locationFlyersTitle', {
          locationName,
        });
      },
    },
  },
  '/?.*/flyer/.*': {
    getTitle(metaObj, locale, t, { flyerData }) {
      let res =
        locale === 'fr-ca' && metaObj.titleFr
          ? metaObj.titleFr
          : locale === 'en-ca'
          ? metaObj.titleEn
          : metaObj.titleEnUs;
      if (flyerData) {
        const date = `${convertTimestampToDate(flyerData.flyer.available_from)} - ${convertTimestampToDate(
          flyerData.flyer.available_to,
        )}`;
        res = t('metaInfo.singleFlyerTitle', {
          storeName: flyerData.flyer.merchant,
          date,
        });
      }
      return res;
    },
    getDescription(metaObj, locale, t, { flyerData }) {
      let res =
        locale === 'fr-ca' && metaObj.descriptionFr
          ? metaObj.descriptionFr
          : locale === 'en-ca'
          ? metaObj.descriptionEn
          : metaObj.descriptionEnUs;
      if (flyerData) {
        res = t('meta.flyerDescription', { store: flyerData.flyer.merchant });
      }
      return res;
    },
    openGraph: {
      getImage({ flyerData }) {
        if (flyerData) {
          return flyerData.flyer.thumbnail_url;
        }
        return '/assets/stores.png';
      },
    },
  },
  '/?.*/search/.*': {
    getTitle(metaObj, locale, t, { slug }) {
      let qry = '';
      let location = LOCATION_FOR_BOT[locale].location;
      if (slug && slug.length === 1) {
        qry = slug[0];
      } else if (slug && slug.length === 2) {
        location = slug[0];
        qry = slug[1];
      }
      const locationName =
        (location && `${extractCityFromLocation(location)}, ${extractStateFromLocation(location)}`) || '';
      return t('metaInfo.searchTitle', { locationName, qry });
    },
    getDescription(metaObj, locale, t, { slug }) {
      let qry = '';
      let location = LOCATION_FOR_BOT[locale].location;
      if (slug && slug.length === 1) {
        qry = slug[0];
      } else if (slug && slug.length === 2) {
        location = slug[0];
        qry = slug[1];
      }
      const locationName =
        (location && `${extractCityFromLocation(location)}, ${extractStateFromLocation(location)}`) || '';
      return t('metaInfo.searchDescription', { locationName, qry });
    },
  },
  '(/?.*/stores/.*)': {
    descriptionEn:
      'Browse the latest [store] [location] flyer. Find the best deals on groceries, appliances and more. Check out the weekly [store] [location] flyer and start saving today!',
    descriptionEnUs:
      'Browse the latest [store] [location] weekly ads. Find the best deals on groceries, appliances and more. Check out the weekly [store] [location] weekly ad and start saving today!',
    descriptionFr:
      "Découvrez les rabais de la circulaire [store] [location]. Profitez des rabais de la circulaire [store] en ligne. Économisez sur vos achats en épicerie, l'électronique et plus!",
    getTitle(metaObj, locale, t, { store = 'Flipp', location }) {
      const storeName = getDisplayStoreName(store);
      const locationName =
        (location && `${extractCityFromLocation(location)}, ${extractStateFromLocation(location)}`) || '';

      if (!locationName) {
        return t('metaInfo.singleStoreTitle', { storeName });
      }

      return t('metaInfo.locationSingleStoreTitle', {
        storeName,
        locationName,
      });
    },
    getDescription(metaObj, locale, t, { store = 'Flipp', location }) {
      const displayStoreName = getDisplayStoreName(store);
      const locationName =
        (location && `${extractCityFromLocation(location)}, ${extractStateFromLocation(location)}`) || '';
      let res =
        locale === 'fr-ca' && metaObj.descriptionFr
          ? metaObj.descriptionFr
          : locale === 'en-ca'
          ? metaObj.descriptionEn
          : metaObj.descriptionEnUs;
      res = res.replace(/\[store\]/g, displayStoreName);
      return addLocationToMeta(res, locationName);
    },
    openGraph: {
      getImage() {
        return '/assets/stores.png';
      },
      getTitle(query, t) {
        const store = query['store'];
        const location = query['location'];
        const storeName = getDisplayStoreName(store);
        const locationName =
          (location && `${extractCityFromLocation(location)}, ${extractStateFromLocation(location)}`) || '';

        const [titleStore, titleLocation, titleEnd] = splitTitle(
          t('metaInfo.og.locationSingleStoreTitle', {
            storeName,
            locationName,
          }),
        );

        if (!locationName) {
          return `${titleStore.replace(/\[store\]/g, storeName)} | ${titleEnd.replace(/\[store\]/g, storeName)}`;
        }

        return `${titleStore.replace(/\[store\]/g, storeName)} | ${addLocationToMeta(
          titleLocation,
          locationName,
        ).replace(/\[store\]/g, storeName)} | ${titleEnd.replace(/\[store\]/g, storeName)}`;
      },
    },
  },
  '/stores': {
    descriptionEn:
      'Check out the latest Walmart Flyer, No Frills Flyer, Canadian Tire Flyer, and weekly flyers from hundreds of stores all in one place. Get the best deals on groceries, electronics, appliances and more.',
    descriptionEnUs:
      'Check out the latest Walmart weekly ad, Home Depot weekly ad, Costco weekly ad, and weekly ads from hundreds of stores all in one place. Get the best deals on groceries, electronics, appliances and more.',
    descriptionFr:
      "Consultez les meilleurs rabais avec la circulaire Walmart, la  circulaire Maxi, la circulaire Canadian Tire et les circulaires en ligne de centaines d'autres magasins à un seul endroit. Les bons prix en épicerie, l'électronique et plus sont ici.",
    getTitle(metaObj, locale, t, { location }) {
      const locationName =
        (location && `${extractCityFromLocation(location)}, ${extractStateFromLocation(location)}`) || '';
      return t('metaInfo.storesTitle', { locationName });
    },
    openGraph: {
      getImage() {
        return `/assets/stores.png`;
      },
    },
  },
  '/.*/stores': {
    descriptionEn: 'Browse the latest [location] flyer and find the best deals in [location]. Start saving now!',
    descriptionEnUs: 'Browse the latest [location] weekly ads and find the best deals in [location]. Start saving now!',
    descriptionFr:
      'Toutes les circulaires en ligne [location]. Circulaires de la semaine prochaine et rabais à [location]. Trouvez des économies ici!',
    getTitle(metaObj, locale, t, { location }) {
      const locationName =
        (location && `${extractCityFromLocation(location)}, ${extractStateFromLocation(location)}`) || '';

      return t('metaInfo.locationStoresTitle', { locationName });
    },
    getDescription(metaObj, locale, t, { location }) {
      const locationName =
        (location && `${extractCityFromLocation(location)}, ${extractStateFromLocation(location)}`) || '';
      const res =
        locale === 'fr-ca' && metaObj.descriptionFr
          ? metaObj.descriptionFr
          : locale === 'en-ca'
          ? metaObj.descriptionEn
          : metaObj.descriptionEnUs;
      return addLocationToMeta(res, locationName);
    },
    openGraph: {
      getImage() {
        return `/assets/stores.png`;
      },
      getTitle(query, t) {
        const locationName = query['location'];
        const location =
          (locationName && `${extractCityFromLocation(locationName)}, ${extractStateFromLocation(locationName)}`) || '';

        return t('metaInfo.og.locationStoresTitle', { location });
      },
    },
  },
};

export default meta;
